import { profile } from "./profile";
import { getScreenshotsForProject } from "../utils/autoGeneratedScreenshots";

export interface Project {
  id: string;
  title: string;
  description: string;
  type: 'web' | 'mobile' | 'game' | 'other';
  subType?: string;
  technologies: string[];
  imageUrl?: string;
  websitePreviewImage?: string; // Full-page website screenshot for scrolling preview
  githubUrl?: string;
  deployedUrl?: string;
  websiteUrl?: string;
  isPrivate?: boolean;
  screenshots?: string[];
  videos?: string[];
  // Mobile app preview options
  expoSnackId?: string; // Expo Snack ID for interactive preview
  expoUrl?: string; // Expo URL for QR code preview
  playStoreUrl?: string;
  appStoreUrl?: string;
}

// Helper function to add dynamic screenshots to projects
// Helper function to add dynamic screenshots to projects
const addScreenshotsToProject = (project: Omit<Project, 'screenshots'> & { screenshots?: string[] }): Project => {
  const screenshots = (project.type === 'mobile' || project.type === 'web')
    ? getScreenshotsForProject(project.id, project.type)
    : [];

  return {
    ...project,
    screenshots: screenshots.length > 0 ? screenshots : project.screenshots
  };
};

export const projects: Project[] = [
  {
    id: 'afm-solution',
    title: 'AFM SOLUTION',
    description: 'Official website of AFM SOLUTION, a premier tax consultancy and digital invoicing platform. Features FBR integration, automated tax compliance, return filing services, and a comprehensive tax calculator for Pakistani businesses.',
    type: 'web' as const,
    technologies: ['React', 'NodeJs', 'Vite', 'CSS', 'TypeScript', 'JavaScript', 'Docker'],
    deployedUrl: 'https://afmsolution.tech',
    websitePreviewImage: '/websites/afm-solution.png',
    githubUrl: `${profile.contact.github}/AFM-SOLUTION`,
    isPrivate: true,
  },
  {
    id: 'coolsipster',
    title: 'Coolsipster',
    description: 'A premium full-stack e-commerce platform for sustainable drinkware. Features a high-performance Next.js 15 storefront with fluid Framer Motion animations, a comprehensive admin dashboard, and a robust Node.js backend using MongoDB, Redis, and Stripe for secure payments and order processing.',
    type: 'web' as const,
    technologies: [
      'Next.js',
      'React',
      'TypeScript',
      'Tailwind CSS',
      'Node.js',
      'Express',
      'MongoDB',
      'Redis',
      'Docker'
    ],
    deployedUrl: 'https://www.coolsipster.com',
    websitePreviewImage: '/websites/coolsipster.png',
    githubUrl: `${profile.contact.github}/CoolSipster`,
    isPrivate: true,
  },
  {
    id: 'teezyon',
    title: 'Teezyon',
    description: "Official website including all the tools of Teezyon, Pakistan's first AI-powered fashion customization platform.",
    type: 'web' as const,
    technologies: ['React', 'NodeJs', 'Vite', 'CSS', 'TypeScript', 'JavaScript', 'Docker'],
    deployedUrl: 'https://www.teezyon.com/',
    websitePreviewImage: '/websites/teezyon.png',
    githubUrl: `${profile.contact.github}/Teezyon`,
    isPrivate: true,
  },
  {
    id: 'octosystems-web',
    title: 'Octosystems Website',
    description: 'Official website of Octosystems, showcasing their IoT solutions, smart automation products, industrial projects, and mobile app for managing devices.',
    type: 'web' as const,
    technologies: ['React', 'CRA', 'CSS', 'JavaScript', 'PHP'],
    deployedUrl: 'https://octosystems.com.br/',
    websitePreviewImage: '/websites/octosystems.png',
    githubUrl: `${profile.contact.github}/Octosystems`,
    isPrivate: true,
  },
  {
    id: 'octosystems-mobile',
    title: 'Octosystems',
    description: 'A React Native mobile app for managing Bluetooth Low Energy (BLE) devices with features like location tracking, secure authentication, background task handling, and real-time notifications. Built for industrial and enterprise use cases such as asset tracking, device provisioning, and proximity-based access.',
    type: 'mobile' as const,
    technologies: ['React Native', 'Expo', 'TypeScript', 'BLE', 'IoT', 'Android', 'iOS'],
    deployedUrl: 'https://github.com/AteebNoOne/Octosystems/releases/download/v1.0.0-android/app-release.apk',
    websitePreviewImage: '/mobile-apps/octosystems.jpeg',
    githubUrl: `${profile.contact.github}/Octosystems`,
    isPrivate: true,
  },
  {
    id: 'crm-cyberclosys',
    title: 'CRM Cyberclosys',
    description: 'CRM for Cyberclosys that manages events, projects, and customers.',
    type: 'web' as const,
    technologies: ['React', 'Vite', 'JavaScript', 'TailwindCSS'],
    deployedUrl: 'https://crm-cyberclosys.vercel.app/',
    websitePreviewImage: '/websites/crm-cyberclosys.png',
    githubUrl: `${profile.contact.github}/CRMCyberclosys`,
    isPrivate: true,
  },
  {
    id: 'voice-gpt',
    title: 'VoiceGPT',
    description: 'A voice-powered AI chatbot that uses voice commands to interact with the user. It transcribes the user\'s voice and the Gemini API to generate a response.',
    type: 'web' as const,
    technologies: ['Html', 'Scss', 'JavaScript', 'TypeScript', 'Ionic', 'Angular', 'Python', 'Flask', 'Gemini'],
    deployedUrl: 'https://voicegpt-vfcj.onrender.com/',
    websitePreviewImage: '/websites/voice-gpt.png',
    githubUrl: `${profile.contact.github}/voiceGPTBackend`,
    isPrivate: false
  },
  {
    id: 'gesture-smart',
    title: 'Gesture Smart',
    description: 'A smart mobile app allows you to control your android using gestures using mobile\'s camera and using voice commands using mobile\'s microphone and using Eye gestures using mobile\'s camera',
    type: 'mobile' as const,
    technologies: ['React Native', 'Expo', 'TypeScript', 'JavaScript', 'Java', 'Kotlin', 'AI', 'Nodejs', 'Express', 'MongoDB', 'AssemblyAI'],
    deployedUrl: 'https://github.com/AteebNoOne/GestureSmart/blob/V2_Code/README.md',
    websitePreviewImage: '/mobile-apps/gesture-smart/logo.png',
    githubUrl: `${profile.contact.github}/GestureSmart`,
    isPrivate: false,
  },
  {
    id: 'vote-chain',
    title: 'Vote Chain',
    description: 'A decentralized blockchain-powered voting system that ensures transparent, secure, and tamper-proof elections. Built with modern web technologies to provide a seamless voting experience while maintaining the immutability and trust that blockchain technology offers.',
    type: 'web' as const,
    technologies: ['React', 'Vite', 'TypeScript', 'Blockchain', 'IOT'],
    deployedUrl: 'https://vote-chain-two.vercel.app/',
    websitePreviewImage: '/websites/vote-chain.png',
    githubUrl: `${profile.contact.github}/VoteChain`,
    isPrivate: true
  },
  {
    id: 'law-school',
    title: 'Law School',
    description: 'A comprehensive educational platform designed specifically for law students, offering interactive learning modules, case studies, and legal resources. Features a modern interface built with Next.js to facilitate effective legal education and study management.',
    type: 'web' as const,
    technologies: ['Next', 'TypeScript', 'Turbo'],
    deployedUrl: 'https://law-school-teebot.vercel.app/',
    websitePreviewImage: '/websites/law-school.png',
    githubUrl: `${profile.contact.github}/VoteChain`,
    isPrivate: true
  },
  {
    id: 'mallsylo',
    title: 'Mallsylo',
    description: 'A sophisticated multi-vendor e-commerce marketplace featuring comprehensive vendor management, admin dashboards, and customer shopping experiences. Built with a robust full-stack architecture using React frontend and Node.js/MongoDB backend to handle complex e-commerce operations including inventory management, order processing, and payment integration.',
    type: 'web' as const,
    technologies: ['React', 'CRA', 'TypeScript', 'JavaScript', 'MongoDB', 'Nodejs', 'Express'],
    deployedUrl: 'https://mallsylo.vercel.app/',
    websitePreviewImage: '/websites/mallsylo.png',
    githubUrl: `${profile.contact.github}/VoteChain`,
    isPrivate: true
  },
  {
    id: 'tendor-app',
    title: 'Tendor',
    description: 'A native iOS mobile application built with React Native, delivering a smooth and intuitive user experience optimized for Apple devices. Features modern mobile UI/UX patterns and leverages iOS-specific capabilities for enhanced performance and user engagement.',
    type: 'mobile' as const,
    technologies: ['React Native', 'iOS', 'TypeScript'],
    deployedUrl: 'https://apps.apple.com/lc/app/tendor-app/id6450906363',
    websitePreviewImage: '/mobile-apps/default.webp',
    githubUrl: `${profile.contact.github}/VoteChain`,
    isPrivate: true,
  },
  {
    id: 'the-imagination',
    title: 'The Imagination',
    description: 'A premium skincare e-commerce platform offering curated beauty products with an elegant, modern design. Built with React and Vite for optimal performance, featuring MongoDB for robust data management and serverless architecture for scalable operations. Provides a seamless shopping experience for skincare enthusiasts.',
    type: 'web' as const,
    technologies: ['React', 'Vite', 'TypeScript', 'MongoDB', 'Serverless'],
    deployedUrl: 'https://the-imagination.vercel.app/',
    websitePreviewImage: "/websites/the-imagination.png",
    githubUrl: `${profile.contact.github}/VoteChain`,
    isPrivate: true
  },
  {
    id: 'ib-tech',
    title: 'IB Tech',
    description: 'An immersive 3D portfolio website showcasing technical expertise and projects through interactive Three.js experiences. Features cutting-edge web technologies to create engaging visual presentations and demonstrates advanced frontend development skills with modern React architecture.',
    type: 'web' as const,
    technologies: ['React', 'Vite', 'TypeScript', 'ThreeJS'],
    deployedUrl: 'https://ibtech-three.vercel.app/',
    websitePreviewImage: '/websites/ib-tech.png',
    githubUrl: `${profile.contact.github}/ibtech-three`,
    isPrivate: true
  },
  {
    id: 'voash',
    title: 'Voash',
    description: 'An innovative freelancing platform specifically designed for graphic designers, consolidating portfolio management, project coordination, and business operations into one streamlined platform. Features integrated payment processing with Stripe, eliminating the need for multiple apps and providing a "ridiculously good-looking" interface that simplifies the freelance workflow.',
    type: 'web' as const,
    technologies: ['Next', 'TypeScript', 'Turbo', 'Stripe'],
    deployedUrl: 'https://www.voash.com/',
    websitePreviewImage: '/websites/voash.png',
    githubUrl: `${profile.contact.github}/VoteChain`,
    isPrivate: true
  },
  {
    id: 'alvento',
    title: 'Alvento',
    description: 'A premium business portfolio website for Alvento, a global technology company specializing in comprehensive software development solutions. Features a modern, responsive design with dark/light mode support, showcasing services including web development (React, Next.js, Vue.js, Angular), mobile app development (Flutter, React Native, Native iOS/Android), desktop applications (Electron, Tauri, .NET), hardware & embedded systems (ESP32, Arduino, IoT), AI/ML integration (PyTorch, TensorFlow, Computer Vision), and cloud & DevOps solutions. Built with Next.js App Router, TypeScript, and Tailwind CSS, emphasizing performance, SEO optimization, and a premium user experience with charcoal and electric violet brand colors.',
    type: 'web' as const,
    technologies: ['Next.js', 'TypeScript', 'Tailwind CSS', 'App Router', 'ESLint', 'Dark Mode', 'SEO'],
    deployedUrl: 'https://alvento.vercel.app/',
    websitePreviewImage: '/websites/alvento.png',
    githubUrl: `${profile.contact.github}/alvento`,
    isPrivate: true,
  },
  {
    id: 'invoice-manager',
    title: 'Invoice Manager',
    description: 'A comprehensive invoice management system built with React, TypeScript, and Redux. Features include invoice creation with PDF export, dashboard analytics, client/product management, profile customization with logo upload, persistent data storage, and a modern responsive UI with dark/light theme support.',
    type: 'web' as const,
    technologies: ['React', 'TypeScript', 'Redux Toolkit', 'Redux Persist', 'React Router', 'React Hook Form', 'Tailwind CSS', 'Vite', 'ESLint', 'Lucide React', 'html2pdf.js', 'Dark Mode', 'PDF Export'],
    websitePreviewImage: '/websites/invoice-manager/main.png',
    githubUrl: `${profile.contact.github}/InvoiceManager`,
    isPrivate: true
  },
  {
    id: 'flair-home',
    title: 'Flair Home',
    description:
      'Flair Home is a smart scent diffuser control application built with Kotlin Multiplatform Mobile (KMM), delivering a seamless cross-platform experience for both iOS and Android users. The application allows users to connect and manage Flair diffusers through Bluetooth, control fragrance intensity in real time, create personalized schedules, monitor oil levels, and receive proactive alerts for device activity and refills. Built using SwiftUI for iOS, Kotlin UI for Android, and shared Kotlin Multiplatform modules, the project focuses on scalability, performance, and a modern native user experience. The app is part of the Flair Diffusers ecosystem, a luxury fragrance and smart diffuser brand offering premium cold-air diffusion solutions for homes and commercial spaces.',
    type: 'mobile' as const,
    technologies: [
      'Kotlin Multiplatform Mobile (KMM)',
      'Kotlin',
      'Swift',
      'SwiftUI',
      'Kotlin UI',
      'Android',
      'iOS',
      'Bluetooth',
      'Cross Platform',
      'IoT'
    ],
    websiteUrl: 'https://flairdiffusers.com/',
    websitePreviewImage: '/mobile-apps/flair-home/logo.png',
    githubUrl: '',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.flair.diffusers.home',
    appStoreUrl: 'https://apps.apple.com/ca/app/flair-home/id6762539208#information',
    features: [
      'Bluetooth-enabled smart diffuser connectivity',
      'Real-time fragrance intensity control',
      'Personalized scheduling and automation',
      'Oil level monitoring and refill alerts',
      'Cross-platform shared business logic with KMM',
      'Modern native UI using SwiftUI and Kotlin UI',
      'Offline schedule execution without Wi-Fi',
      'Support for multiple Flair diffuser models'
    ],
    isPrivate: true,
  },
  {
    id: 'advenix-solution',
    title: 'ADVENIX SOLUTIONS',
    description: 'Official website of Advenix Solutions, a premier AI-powered digital marketing agency specializing in search engine optimization (SEO), Generative Engine Optimization (GEO), paid PPC/Meta campaigns, and high-converting web platforms.',
    type: 'web' as const,
    technologies: ['Next.js', 'React', 'TypeScript', 'Tailwind CSS'],
    deployedUrl: 'https://www.advenixsolutions.com/',
    websitePreviewImage: '/websites/advenix-solutions.png',
    githubUrl: `${profile.contact.github}/VoteChain`,
    isPrivate: true,
  },
  {
    id: 'wool-together',
    title: 'Wool Together',
    description: 'Official website of Wool Together, an artisanal handcrafted crochet brand featuring custom woven designs, interactive pattern showcases, and a seamless e-commerce platform for handcrafted items.',
    type: 'web' as const,
    subType: '3D',
    technologies: ['Next.js', 'React', 'TypeScript', 'Tailwind CSS', 'E-Commerce'],
    deployedUrl: 'https://wooltogether.iteebot.com/',
    websitePreviewImage: '/websites/wool-together.png',
    githubUrl: `${profile.contact.github}/WoolTogether`,
    isPrivate: true,
  },
  {
    id: 'virlio',
    title: 'Virlio',
    description: 'Official website of Virlio, an AI-powered viral meme generator and social media content creation suite designed for creators, marketers, and brands.',
    type: 'web' as const,
    technologies: ['Next.js', 'React', 'TypeScript', 'Tailwind CSS', 'AI / ML'],
    deployedUrl: 'https://virlio.iteebot.com/',
    websitePreviewImage: '/websites/virlio.png',
    githubUrl: `${profile.contact.github}/Virlio`,
    isPrivate: true,
  },
  {
    id: 'mehr-henna',
    title: 'MEHR — Henna, reimagined',
    description: 'An ancient art form, drawn for the modern world. A contemporary henna atelier working in botanical composition, bridal ceremony and editorial art.',
    type: 'web' as const,
    subType: '3D',
    technologies: ['Next.js', 'React', 'ThreeJS', 'Tailwind CSS'],
    deployedUrl: 'https://mehr.iteebot.com/',
    websitePreviewImage: '/websites/mehr.png',
    githubUrl: profile.contact.github,
    isPrivate: true,
  },
  {
    id: 'gilded-crumb',
    title: 'Gilded Crumb',
    description: 'A custom cake studio, made by hand. From imagination to cake.',
    type: 'web' as const,
    subType: '3D',
    technologies: ['Next.js', 'React', 'ThreeJS', 'Tailwind CSS'],
    deployedUrl: 'https://cakes.iteebot.com/',
    websitePreviewImage: '/websites/cakes.png',
    githubUrl: profile.contact.github,
    isPrivate: true,
  },
  {
    id: 'around-teebot-art',
    title: 'Around Teebot Art Studio',
    description: 'Paintings shaped by memory, light and emotion.',
    type: 'web' as const,
    subType: '3D',
    technologies: ['Next.js', 'React', 'ThreeJS', 'Tailwind CSS'],
    deployedUrl: 'https://art.iteebot.com/',
    websitePreviewImage: '/websites/art.png',
    githubUrl: profile.contact.github,
    isPrivate: true,
  },
  {
    id: 'private-farmhouse',
    title: 'A Day Out, Made Yours',
    description: 'Experience a premium private farmhouse retreat with pool, BBQ, sports, and more.',
    type: 'web' as const,
    subType: '3D',
    technologies: ['Next.js', 'React', 'ThreeJS', 'Tailwind CSS'],
    deployedUrl: 'https://farm.iteebot.com/',
    websitePreviewImage: '/websites/farm.png',
    githubUrl: profile.contact.github,
    isPrivate: true,
  },
  {
    id: 'elane-cosmetics',
    title: 'ELANE COSMETICS',
    description: 'Skincare, Refined.',
    type: 'web' as const,
    subType: '3D',
    technologies: ['Next.js', 'React', 'ThreeJS', 'Tailwind CSS'],
    deployedUrl: 'https://elane.iteebot.com/',
    websitePreviewImage: '/websites/elane.png',
    githubUrl: profile.contact.github,
    isPrivate: true,
  }
].map(addScreenshotsToProject);